/**
 * The typed API client, driven by the route table (PLAN §10.1: "typed client
 * generation" from the contract).
 *
 *     client.call('listRows', { params: { rundownId }, query: { parentId }, body: null })
 *
 * is typed from `ROUTES.listRows`'s zod schemas: the request is validated
 * against them before it is sent (a bad argument never reaches the server),
 * and the response is validated after, so a response that does not match
 * the contract is an error naming the drift, never a silently mis-typed
 * value. Failures come back as `ApiError`, carrying the contract's typed
 * `code` (`errors.ts`).
 *
 * **Responses are read in the host's `ContractParsing` mode** (D35,
 * `@acp/api-contract/parsing.js`): a running SPA, CLI or node reads them
 * `'tolerant'` — a field a newer server added is dropped, not refused, so an
 * additive deploy never breaks an older client — and tests read them
 * `'strict'`, so a field the contract does not declare still fails CI.
 * Requests are always validated strictly: this client never sends a field
 * the contract does not know.
 *
 * Shared by the `acp` CLI and the SPA (moved here from `apps/cli`): the HTTP
 * transport and the token source are injected, so it runs in Node and in a
 * browser alike.
 */
import { type ErrorCodeWire, errorCodeWireSchema, errorResponseWireSchema } from '@acp/api-contract/errors.js';
import { z } from 'zod';
import type { FetchPort } from './ports.js';
import { type RouteDef, ROUTES } from '@acp/api-contract/routes.js';
import { type ContractParsing, parseContract } from '@acp/api-contract/parsing.js';

/*
 * The call shape derived from the route table: a route added to `ROUTES` is
 * callable with no plumbing — `client.call('newRoute', …)` is typed from its schemas.
 */
export type RouteId = keyof typeof ROUTES;
export type Routes = typeof ROUTES;

type InputOf<S> = S extends z.ZodType ? z.input<S> : null;

/** What a call passes: every part explicitly, null where the route has none. */
export type RouteInput<R extends RouteDef> = {
  readonly params: InputOf<R['params']>;
  readonly query: InputOf<R['query']>;
  readonly body: InputOf<R['body']>;
};

export type RouteOutput<R extends RouteDef> = z.output<R['response']>;

export type ApiIssue = { readonly path: string; readonly message: string };

/** A non-2xx answer, decoded. `code` is the contract's when the server spoke it, else the raw code string. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCodeWire | string;
  readonly issues: readonly ApiIssue[];
  readonly retryAfterSeconds: number | null;

  constructor(status: number, code: ErrorCodeWire | string, message: string, issues: readonly ApiIssue[], retryAfterSeconds: number | null) {
    const detail = issues.map((i) => `\n  ${i.path === '' ? '(request)' : i.path}: ${i.message}`).join('');
    super(`${code} (HTTP ${String(status)}): ${message}${detail}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.issues = issues;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Whether `code` is one of the contract's error codes. */
export function isContractErrorCode(code: string): code is ErrorCodeWire {
  return errorCodeWireSchema.safeParse(code).success;
}

export type BuiltRequest = { readonly method: string; readonly url: string; readonly body: string | null };

function validated(what: string, schema: z.ZodType, value: unknown, parsing: ContractParsing): void {
  const result = parseContract(schema, value, parsing);
  if (!result.success) throw new Error(`invalid ${what}:\n${z.prettifyError(result.error)}`);
}

/**
 * The HTTP request for a call: path params substituted, query serialised, body JSON — each validated first.
 * `bodies`: how the body is validated. `'strict'` for a client that builds its own bodies; `'tolerant'` for one that
 * writes back records it read `'preserving'` (the node, D37): the fields it keeps without knowing them are sent as
 * they came, and the receiver judges them.
 */
export function buildRequest(route: RouteDef, input: { params: unknown; query: unknown; body: unknown }, baseUrl: string, bodies: ContractParsing): BuiltRequest {
  let path = route.path;
  if (route.params !== null) {
    validated(`${route.id} path parameters`, route.params, input.params, 'strict');
    const params = z.record(z.string(), z.string()).parse(input.params);
    path = path.replaceAll(/\{([^}]+)\}/g, (_match, name: string) => encodeURIComponent(params[name] ?? ''));
  }
  // Serialised by hand rather than with `URLSearchParams`: this package names
  // no host API (see ports.ts), and `encodeURIComponent` is the language's own.
  const search: string[] = [];
  if (route.query !== null && input.query !== null) {
    validated(`${route.id} query`, route.query, input.query, 'strict');
    for (const [key, value] of Object.entries(z.record(z.string(), z.unknown()).parse(input.query))) {
      if (value !== undefined) search.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  let body: string | null = null;
  if (route.body !== null) {
    validated(`${route.id} request body`, route.body, input.body, bodies);
    body = JSON.stringify(input.body);
  }
  const qs = search.join('&');
  return { method: route.method, url: `${baseUrl}${path}${qs === '' ? '' : `?${qs}`}`, body };
}

const looseErrorSchema = z.object({ error: z.object({ code: z.string(), message: z.string() }) });

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** A non-success answer as an `ApiError`: the contract's error body, else any `{error:{code,message}}`, else the status. */
export function decodeError(status: number, text: string): ApiError {
  const json = parseJson(text);
  const strict = errorResponseWireSchema.safeParse(json);
  if (strict.success) {
    const e = strict.data.error;
    return new ApiError(status, e.code, e.message, e.issues, e.retryAfterSeconds);
  }
  const loose = looseErrorSchema.safeParse(json);
  if (loose.success) return new ApiError(status, loose.data.error.code, loose.data.error.message, [], null);
  const snippet = text.trim().slice(0, 200);
  return new ApiError(status, `http-${String(status)}`, snippet === '' ? 'no error body' : snippet, [], null);
}

/** A success answer, validated against the route's response schema (unknown fields dropped when `parsing` is tolerant). */
export function decodeResponse(route: RouteDef, status: number, text: string, parsing: ContractParsing): unknown {
  if (status !== route.successStatus) {
    throw new Error(`contract drift: ${route.id} answered HTTP ${String(status)}, the contract says ${String(route.successStatus)}`);
  }
  const json = parseJson(text);
  if (json === undefined) throw new Error(`contract drift: ${route.id} answered a body that is not JSON`);
  const result = parseContract(route.response, json, parsing);
  if (!result.success) throw new Error(`contract drift: ${route.id}'s response does not match the contract:\n${z.prettifyError(result.error)}`);
  return result.data;
}

export type ApiClient = {
  readonly baseUrl: string;
  readonly call: <K extends RouteId>(id: K, input: RouteInput<Routes[K]>) => Promise<RouteOutput<Routes[K]>>;
};

export type ApiClientDeps = {
  readonly baseUrl: string;
  readonly fetch: FetchPort;
  /** The bearer token; `force` after a 401. Null: send no Authorization header. */
  readonly token: ((force: boolean) => Promise<string>) | null;
  /** How responses are read: `'tolerant'` in a running client, `'strict'` in tests (see the module comment). */
  readonly responses: ContractParsing;
};

export function createApiClient(deps: ApiClientDeps): ApiClient {
  const send = async (request: BuiltRequest, force: boolean) => {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (request.body !== null) headers['content-type'] = 'application/json';
    if (deps.token !== null) headers['authorization'] = `Bearer ${await deps.token(force)}`;
    const response = await deps.fetch(request.url, { method: request.method, headers, body: request.body });
    return { status: response.status, text: await response.text() };
  };

  const callRoute = async (route: RouteDef, input: { params: unknown; query: unknown; body: unknown }): Promise<unknown> => {
    const request = buildRequest(route, input, deps.baseUrl, deps.responses === 'preserving' ? 'tolerant' : 'strict');
    let answer = await send(request, false);
    // One retry with a refreshed token: the cached one can be revoked or the
    // clocks can disagree about its expiry.
    if (answer.status === 401 && deps.token !== null) answer = await send(request, true);
    if (answer.status < 200 || answer.status > 299) throw decodeError(answer.status, answer.text);
    return decodeResponse(route, answer.status, answer.text, deps.responses);
  };

  // The typed signature is an overload over an untyped implementation: the
  // value returned was just parsed by exactly `ROUTES[id].response`, so
  // it IS that schema's output, but TypeScript cannot follow a schema chosen
  // by a generic key through `safeParse`. One overload here instead of an
  // `as` at every call site.
  function call<K extends RouteId>(id: K, input: RouteInput<Routes[K]>): Promise<RouteOutput<Routes[K]>>;
  function call(id: RouteId, input: { params: unknown; query: unknown; body: unknown }): Promise<unknown> {
    return callRoute(ROUTES[id], input);
  }

  return { baseUrl: deps.baseUrl, call };
}

/** Every page of a paged listing, concatenated. */
export async function allPages<T>(fetchPage: (pageToken: string | undefined) => Promise<{ items: readonly T[]; nextPageToken: string | null }>): Promise<T[]> {
  const out: T[] = [];
  let token: string | undefined;
  for (;;) {
    const page = await fetchPage(token);
    out.push(...page.items);
    if (page.nextPageToken === null) return out;
    token = page.nextPageToken;
  }
}
